import { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Trash2, Save, X, Upload, Eye } from 'lucide-react';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import { FileText } from 'lucide-react';
import { Confirm, useNotify } from 'react-admin';
import * as XLSX from 'xlsx';
import TemplateDataDetail from './TemplateDataDetail.tsx';

declare const process: {
	env: {
		REACT_APP_API_URL?: string;
	};
};

export interface COATemplateItem {
	id?: number;
	batchNumber?: string;
	parameter: string;
	classification: string;
	testMethod: string;
	uom: string;
}

export interface COATemplate {
	id: string;
	batchNumber: string;
	productDescription: string;
	specNumber: string;
	sampleSubmissionRefNumber: string;
	manufactureDate: string;
	expiryDate: string;
	items: COATemplateItem[];
}

interface TemplateDataListProps {
	templates: COATemplate[];
	onUpdate: (templates: COATemplate[]) => void;
}

export default function TemplateDataList({ templates = [], onUpdate }: TemplateDataListProps) {
	const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';
	const [isAdding, setIsAdding] = useState(false);
	const [editingId, setEditingId] = useState<string | null>(null);
	const [formData, setFormData] = useState<Omit<COATemplate, 'id'>>({
		batchNumber: '',
		productDescription: '',
		specNumber: '',
		sampleSubmissionRefNumber: '',
		manufactureDate: '',
		expiryDate: '',
		items: []
	});
	const [newItem, setNewItem] = useState<COATemplateItem>({
		parameter: '',
		classification: 'General',
		testMethod: '',
		uom: '',
	});

	const [templatesState, setTemplatesState] = useState<COATemplate[]>(templates || []);
	const [page, setPage] = useState(1);
	const [perPage, setPerPage] = useState(10);
	const [batchNumberQuery, setBatchNumberQuery] = useState('');
	const [listFilter, setListFilter] = useState<{ batchNumber: string } | null>(null);
	const [totalCount, setTotalCount] = useState<number>(0);
	const [loadingList, setLoadingList] = useState(false);
	const notify = useNotify();
	const [saving, setSaving] = useState(false);
	const [importing, setImporting] = useState(false);
	const importFileInputRef = useRef<HTMLInputElement | null>(null);
	const [detailTemplate, setDetailTemplate] = useState<COATemplate | null>(null);
	const [loadingDetail, setLoadingDetail] = useState(false);
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
	const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

	const normalizeHeader = (value: string) => value.trim().toLowerCase().replace(/[_\s\-/()]+/g, '');

	const extractClassificationName = (value: string): string => {
		const text = String(value ?? '').trim();
		if (!text) return 'General';

		const firstLine = text.split(/\r?\n/)[0]?.trim() || text;
		return firstLine.replace(/[\s:;，、。]+$/g, '') || 'General';
	};

	const toIsoDate = (value: unknown): string => {
		if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toISOString().slice(0, 10);
		if (typeof value === 'number') {
			const jsDate = XLSX.SSF.parse_date_code(value);
			if (jsDate) {
				const d = new Date(jsDate.y, jsDate.m - 1, jsDate.d);
				return d.toISOString().slice(0, 10);
			}
		}
		if (typeof value === 'string') {
			const trimmed = value.trim();
			if (!trimmed) return '';
			const normalized = trimmed.replace(/\./g, '/').replace(/-/g, '/');
			const parts = normalized.split('/').map((p) => p.trim());
			if (parts.length === 3) {
				const y = Number(parts[0]);
				const m = Number(parts[1]);
				const d = Number(parts[2]);
				if (Number.isFinite(y) && Number.isFinite(m) && Number.isFinite(d)) {
					const date = new Date(y, m - 1, d);
					if (!Number.isNaN(date.getTime())) return date.toISOString().slice(0, 10);
				}
			}
			const date = new Date(trimmed);
			if (!Number.isNaN(date.getTime())) return date.toISOString().slice(0, 10);
		}
		return '';
	};

	const findLabeledValue = (rows: any[][], label: string): unknown => {
		const target = normalizeHeader(label);
		const rowLimit = Math.min(rows.length, 140);
		for (let r = 0; r < rowLimit; r++) {
			const row = rows[r] || [];
			for (let c = 0; c < row.length; c++) {
				const current = normalizeHeader(String(row[c] ?? ''));
				if (!current || !current.includes(target)) continue;

				const sameRowCandidates = [row[c + 1], row[c + 2], row[c + 3], row[c + 4]];
				for (const value of sameRowCandidates) {
					if (String(value ?? '').trim()) return value;
				}

				for (let rr = r + 1; rr <= Math.min(r + 2, rowLimit - 1); rr++) {
					const belowRow = rows[rr] || [];
					for (let cc = c; cc <= Math.min(c + 3, belowRow.length - 1); cc++) {
						const value = belowRow[cc];
						if (String(value ?? '').trim()) return value;
					}
				}

				const inlineParts = String(row[c] ?? '').split(':').slice(1).map((part) => part.trim()).filter(Boolean);
				if (inlineParts.length > 0) return inlineParts.join(': ');
			}
		}
		return '';
	};

	const findLabelValueAcrossSheets = (workbook: XLSX.WorkBook, label: string): unknown => {
		for (const sheetName of workbook.SheetNames) {
			const sheet = workbook.Sheets[sheetName];
			if (!sheet) continue;
			const rows = XLSX.utils.sheet_to_json<any[]>(sheet, { header: 1, defval: '' }) as any[][];
			const value = findLabeledValue(rows, label);
			if (String(value ?? '').trim()) return value;
		}
		return '';
	};

	const scoreCoATemplateSheet = (rows: any[][]): number => {
		let score = 0;
		for (let r = 0; r < Math.min(rows.length, 140); r++) {
			const row = rows[r] || [];
			const rowText = row.map((cell) => normalizeHeader(String(cell ?? ''))).join(' ');
			if (!rowText) continue;

			if (rowText.includes('productdescription')) score += 6;
			if (rowText.includes('batchnumber')) score += 6;
			if (rowText.includes('specnumber')) score += 6;
			if (rowText.includes('samplesubmissionrefnumber')) score += 6;
			if (rowText.includes('manufacturedate')) score += 4;
			if (rowText.includes('expirydate')) score += 4;
			if (rowText.includes('chemistry')) score += 8;
			if (rowText.includes('parameter')) score += 8;
			if (rowText.includes('testmethod')) score += 8;
			if (rowText.includes('uom')) score += 8;
			if (rowText.includes('testresult')) score += 4;
		}
		return score;
	};

	const locateCoATemplateSheet = (workbook: XLSX.WorkBook): XLSX.WorkSheet => {
		let bestSheet: XLSX.WorkSheet | null = null;
		let bestScore = -1;

		for (const sheetName of workbook.SheetNames) {
			const sheet = workbook.Sheets[sheetName];
			if (!sheet) continue;
			const rows = XLSX.utils.sheet_to_json<any[]>(sheet, { header: 1, defval: '' }) as any[][];
			const sheetScore = scoreCoATemplateSheet(rows);
			if (sheetScore > bestScore) {
				bestScore = sheetScore;
				bestSheet = sheet;
			}
		}

		if (!bestSheet) {
			throw new Error('CoA Template worksheet not found in uploaded file');
		}

		return bestSheet;
	};

	const normalizeImportMessage = (error: unknown): string => {
		if (typeof error === 'string') return error;
		if (error instanceof Error) return error.message;
		if (error && typeof error === 'object') {
			const maybeMessage = (error as { message?: unknown }).message;
			if (typeof maybeMessage === 'string' && maybeMessage.trim()) return maybeMessage;
		}
		return 'Unknown error';
	};

	const parseCoATemplateSheet = (sheet: XLSX.WorkSheet): Omit<COATemplate, 'id'> => {
		const rows = XLSX.utils.sheet_to_json<any[]>(sheet, { header: 1, defval: '' }) as any[][];
		const hasEnglishPrefix = (value: unknown, prefixes: string[]) => {
			const text = String(value ?? '').trim().toLowerCase();
			return prefixes.some((prefix) => text.startsWith(prefix));
		};

		const pickMostFrequentIndex = (indexes: number[]) => {
			if (indexes.length === 0) return -1;
			const counter = new Map<number, number>();
			for (const index of indexes) {
				counter.set(index, (counter.get(index) ?? 0) + 1);
			}
			let winner = indexes[0];
			let winnerCount = 0;
			for (const [index, count] of counter.entries()) {
				if (count > winnerCount) {
					winner = index;
					winnerCount = count;
				}
			}
			return winner;
		};

		const productDescriptionRaw = findLabeledValue(rows, 'Product Description');
		const specNumberRaw = findLabeledValue(rows, 'Spec Number');
		const batchNumberRaw = findLabeledValue(rows, 'Batch Number');
		const manufactureDateRaw = findLabeledValue(rows, 'Manufacture Date');
		const expiryDateRaw = findLabeledValue(rows, 'Expiry Date');

		const methodCandidates: number[] = [];
		const uomCandidates: number[] = [];
		const resultCandidates: number[] = [];
		const classificationCandidates: number[] = [];
		const anchorRows: number[] = [];

		for (let r = 0; r < rows.length; r++) {
			const row = rows[r] || [];
			let rowHasMethod = false;
			let rowHasUom = false;

			for (let c = 0; c < row.length; c++) {
				const cell = row[c];
				if (hasEnglishPrefix(cell, ['test method', 'method'])) {
					methodCandidates.push(c);
					rowHasMethod = true;
				}
				if (hasEnglishPrefix(cell, ['uom', 'unit'])) {
					uomCandidates.push(c);
					rowHasUom = true;
				}
				if (hasEnglishPrefix(cell, ['test result', 'result'])) {
					resultCandidates.push(c);
				}
				if (hasEnglishPrefix(cell, ['classification', 'class', 'category', 'group'])) {
					classificationCandidates.push(c);
				}
			}

			if (hasEnglishPrefix(row[0], ['parameter']) && rowHasMethod && rowHasUom) {
				anchorRows.push(r);
			}
		}

		const parameterIdx = 0;
		const testMethodIdx = pickMostFrequentIndex(methodCandidates);
		const uomIdx = pickMostFrequentIndex(uomCandidates);
		const testResultIdx = pickMostFrequentIndex(resultCandidates);
		const classificationIdx = pickMostFrequentIndex(classificationCandidates);

		if (testMethodIdx < 0 || uomIdx < 0) {
			throw new Error('Unable to locate CoA Template columns (Test Method/UOM).');
		}

		const startRow = anchorRows.length > 0 ? Math.min(...anchorRows) + 1 : 0;
		const headerParameterText = String((rows[startRow] || [])[parameterIdx] ?? '').trim();
		let currentClassification = extractClassificationName(headerParameterText);

		const items: COATemplateItem[] = [];
		let emptyRowStreak = 0;
		for (let i = startRow; i < rows.length; i++) {
			const row = rows[i] || [];
			const parameter = parameterIdx >= 0 ? String(row[parameterIdx] ?? '').trim() : '';
			const testMethod = testMethodIdx >= 0 ? String(row[testMethodIdx] ?? '').trim() : '';
			const uom = uomIdx >= 0 ? String(row[uomIdx] ?? '').trim() : '';
			const testResult = testResultIdx >= 0 ? String(row[testResultIdx] ?? '').trim() : '';
			const classificationFromRow = classificationIdx >= 0 ? String(row[classificationIdx] ?? '').trim() : '';
			const parameterKey = normalizeHeader(parameter);
			const isMetaRow = [
				'productdescription',
				'batchnumber',
				'specnumber',
				'samplesubmissionrefnumber',
				'manufacturedate',
				'expirydate',
			].includes(parameterKey);
			const isSectionHeaderRow =
				!!parameter &&
				hasEnglishPrefix(testMethod, ['test method', 'method']) &&
				hasEnglishPrefix(uom, ['uom', 'unit']);

			const isClassificationOnlyRow = !!parameter && !testMethod && !uom;

			if (isMetaRow) {
				continue;
			}

			if (isSectionHeaderRow || isClassificationOnlyRow) {
				currentClassification = extractClassificationName(parameter);
				continue;
			}

			if (!parameter && !testMethod && !uom && !testResult) {
				emptyRowStreak += 1;
				if (items.length > 0 && emptyRowStreak >= 6) break;
				continue;
			}

			// Skip wrapped/continued rows that only carry a method-like value without a parameter.
			if (!parameter && !!testMethod && !uom) {
				continue;
			}

			emptyRowStreak = 0;

			items.push({
				parameter: parameter || testMethod || 'N/A',
				classification: classificationFromRow || currentClassification,
				testMethod: testMethod || parameter || 'N/A',
				uom: uom || '-',
				// Test Result is intentionally ignored and not persisted for COATemplateItems.
			});
		}

		return {
			productDescription: String(productDescriptionRaw ?? '').trim(),
			specNumber: String(specNumberRaw ?? '').trim(),
			batchNumber: String(batchNumberRaw ?? '').trim(),
			sampleSubmissionRefNumber: '',
			manufactureDate: toIsoDate(manufactureDateRaw),
			expiryDate: toIsoDate(expiryDateRaw),
			items,
		};
	};

	const fetchTemplates = useCallback(async (p: number, pp: number, filterObj: any = null) => {
		setLoadingList(true);
		try {
			const start = (p - 1) * pp;
			const end = start + pp - 1;
			const params = new URLSearchParams();
			params.set('range', `[${start},${end}]`);
			params.set('perPage', String(pp));
			if (filterObj) params.set('filter', JSON.stringify(filterObj));

			const token = localStorage.getItem('token');
			const res = await fetch(`${API_URL}/COATemplates?${params.toString()}`, {
				method: 'GET',
				headers: {
					...(token ? { Authorization: `Bearer ${token}` } : {})
				}
			});

			if (!res.ok) throw new Error(`HTTP ${res.status}`);

			const list = await res.json();
			const contentRange = res.headers.get('Content-Range') || '';
			const m = /\/(\d+)$/.exec(contentRange);
			const total = m ? parseInt(m[1], 10) : list.length;

			setTemplatesState(list || []);
			setTotalCount(total);
			setPage(p);
			setPerPage(pp);
		} catch (err) {
			console.error('Failed to load templates', err);
		} finally {
			setLoadingList(false);
		}
	}, [API_URL]);

	useEffect(() => {
		fetchTemplates(page, perPage, listFilter);
	}, [fetchTemplates, page, perPage, listFilter]);

	const handleSearchByBatchNumber = () => {
		const keyword = batchNumberQuery.trim();
		setPage(1);
		setListFilter(keyword ? { batchNumber: keyword } : null);
	};

	const handleResetBatchNumberSearch = () => {
		setBatchNumberQuery('');
		setPage(1);
		setListFilter(null);
	};

	const handleAddItem = () => {
		if (newItem.parameter && newItem.classification && newItem.testMethod && newItem.uom) {
			const itemToAdd: COATemplateItem = {
				parameter: newItem.parameter,
				classification: newItem.classification,
				testMethod: newItem.testMethod,
				uom: newItem.uom,
			};

			setFormData({
				...formData,
				items: [...formData.items, itemToAdd]
			});
			setNewItem({
				parameter: '',
				classification: 'General',
				testMethod: '',
				uom: '',
			});
		}
	};

	const handleRemoveItem = (index: number) => {
		setFormData({
			...formData,
			items: formData.items.filter((_, i) => i !== index)
		});
	};

	const handleSave = useCallback(async () => {
		if (!(formData.batchNumber && formData.productDescription && formData.specNumber && formData.sampleSubmissionRefNumber && formData.manufactureDate && formData.expiryDate && formData.items.length > 0)) {
			notify('Please fill all required template fields and add at least one test item', { type: 'warning' });
			return;
		}

		setSaving(true);
		try {
			const dto = {
				BatchNumber: formData.batchNumber,
				ProductDescription: formData.productDescription,
				SpecNumber: formData.specNumber,
				SampleSubmissionRefNumber: formData.sampleSubmissionRefNumber,
				ManufactureDate: formData.manufactureDate,
				ExpiryDate: formData.expiryDate,
				Items: formData.items.map(i => ({
					Parameter: i.parameter,
					Classification: i.classification,
					TestMethod: i.testMethod,
					Uom: i.uom,
				}))
			};

			const token = localStorage.getItem('token');

			let res: Response;
			if (editingId) {
				res = await fetch(`${API_URL}/COATemplates/${editingId}`, {
					method: 'PUT',
					headers: {
						'Content-Type': 'application/json',
						...(token ? { Authorization: `Bearer ${token}` } : {})
					},
					body: JSON.stringify(dto)
				});
			} else {
				res = await fetch(`${API_URL}/COATemplates`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						...(token ? { Authorization: `Bearer ${token}` } : {})
					},
					body: JSON.stringify(dto)
				});
			}

			if (!res.ok) {
				const text = await res.text();
				throw new Error(text || `HTTP ${res.status}`);
			}

			const returned = await res.json().catch(() => null);

			if (editingId) {
				if (typeof onUpdate === 'function') {
					onUpdate(templates.map(s => s.id === editingId ? { ...(returned ?? { id: editingId }), ...formData } : s));
				}
				setEditingId(null);
			} else {
				const newTemplate: COATemplate = {
					id: (returned && returned.id) ? String(returned.id) : Date.now().toString(),
					...formData
				};
				if (typeof onUpdate === 'function') {
					onUpdate([...templates, newTemplate]);
				}
			}

			notify('Template saved successfully', { type: 'success' });
			try {
				fetchTemplates(page, perPage, listFilter);
			} catch (e) {
			}
			setIsAdding(false);
			setFormData({ batchNumber: '', productDescription: '', specNumber: '', sampleSubmissionRefNumber: '', manufactureDate: '', expiryDate: '', items: [] });
		} catch (error: any) {
			console.error('Save template error', error);
			notify(`Save template failed: ${error?.message || 'Unknown error'}`, { type: 'error' });
		} finally {
			setSaving(false);
		}
	}, [API_URL, formData, editingId, templates, onUpdate, notify, fetchTemplates, page, perPage, listFilter]);

	const handleDeleteClick = (id: string) => {
		setPendingDeleteId(id);
		setDeleteConfirmOpen(true);
	};

	const handleConfirmDelete = () => {
		const id = pendingDeleteId;
		if (!id) return;

		(async () => {
				try {
					const token = localStorage.getItem('token');
					const res = await fetch(`${API_URL}/COATemplates/${id}`, {
						method: 'DELETE',
						headers: {
							...(token ? { Authorization: `Bearer ${token}` } : {})
						}
					});
					if (!res.ok) throw new Error(`HTTP ${res.status}`);
					const nextTotal = Math.max(0, totalCount - 1);
					const nextTotalPages = Math.max(1, Math.ceil(nextTotal / perPage));
					const targetPage = Math.min(page, nextTotalPages);
					if (targetPage !== page) {
						setPage(targetPage);
					} else {
						fetchTemplates(targetPage, perPage, listFilter);
					}
					if (typeof onUpdate === 'function') onUpdate(templates.filter(template => template.id !== id));
					notify('COA template deleted successfully', { type: 'success' });
				} catch (err: any) {
					console.error('Delete spec error', err);
					notify(`Delete failed: ${err?.message || 'Unknown error'}`, { type: 'error' });
				} finally {
					setDeleteConfirmOpen(false);
					setPendingDeleteId(null);
				}
		})();
	};

	const fetchTemplateById = useCallback(async (id: string): Promise<COATemplate | null> => {
		const token = localStorage.getItem('token');
		const res = await fetch(`${API_URL}/COATemplates/${id}`, {
			method: 'GET',
			headers: {
				...(token ? { Authorization: `Bearer ${token}` } : {}),
			},
		});

		if (!res.ok) return null;
		const template = await res.json();
		return template as COATemplate;
	}, [API_URL]);

	const handleOpenDetail = async (id: string) => {
		setLoadingDetail(true);
		try {
			const template = await fetchTemplateById(id);
			if (!template) {
				notify('Failed to load COA template details', { type: 'error' });
				return;
			}
			setDetailTemplate(template);
		} finally {
			setLoadingDetail(false);
		}
	};

	const handleImportExcel = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		setImporting(true);
		try {
			const arrayBuffer = await file.arrayBuffer();
			const workbook = XLSX.read(arrayBuffer, { type: 'array' });

			const targetSheet = locateCoATemplateSheet(workbook);
			const parsedSpec = parseCoATemplateSheet(targetSheet);
			parsedSpec.sampleSubmissionRefNumber = String(
				findLabelValueAcrossSheets(workbook, 'Sample Submission Ref Number') ||
				findLabelValueAcrossSheets(workbook, 'Sample Submission Ref Number (s)') ||
				parsedSpec.sampleSubmissionRefNumber ||
				''
			).trim();
			if (!parsedSpec.batchNumber || !parsedSpec.productDescription || parsedSpec.items.length === 0) {
				throw new Error('Unable to parse required fields from CoA Template tab.');
			}

			const dto = {
				BatchNumber: parsedSpec.batchNumber,
				ProductDescription: parsedSpec.productDescription,
				SpecNumber: parsedSpec.specNumber,
				SampleSubmissionRefNumber: parsedSpec.sampleSubmissionRefNumber,
				ManufactureDate: parsedSpec.manufactureDate,
				ExpiryDate: parsedSpec.expiryDate,
				Items: parsedSpec.items.map((item) => ({
					Parameter: item.parameter,
					Classification: item.classification,
					TestMethod: item.testMethod,
					Uom: item.uom,
				})),
			};

			const token = localStorage.getItem('token');
			const res = await fetch(`${API_URL}/COATemplates`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					...(token ? { Authorization: `Bearer ${token}` } : {}),
				},
				body: JSON.stringify(dto),
			});

			if (!res.ok) {
				const text = await res.text();
				throw new Error(text || `HTTP ${res.status}`);
			}

			notify(`Excel import success: ${parsedSpec.items.length} template items imported`, { type: 'success' });
			setFormData(parsedSpec);
			setIsAdding(false);
			await fetchTemplates(page, perPage, listFilter);
		} catch (error: any) {
			notify(`Import failed: ${normalizeImportMessage(error)}`, { type: 'error' });
		} finally {
			e.target.value = '';
			setImporting(false);
		}
	};

	const handleCancel = () => {
		setIsAdding(false);
		setEditingId(null);
		setFormData({
			batchNumber: '',
			productDescription: '',
			specNumber: '',
			sampleSubmissionRefNumber: '',
			manufactureDate: '',
			expiryDate: '',
			items: []
		});
	};

	if (detailTemplate) {
		return <TemplateDataDetail template={detailTemplate} onBack={() => setDetailTemplate(null)} />;
	}

	return (
		<div className="space-y-6" style={{ margin: 20 }}>
			<div className="flex items-center justify-between">
				<div>
					<h2 className="text-2xl mb-1">COA Template Management</h2>
					<p className="text-sm text-gray-500">Import analytical files and maintain COA templates</p>
				</div>
				{!isAdding && (
					<div className="flex items-center gap-2">
						<Button
							type="button"
							variant="outline"
							className="border-blue-300 text-blue-700 hover:bg-blue-50"
							onClick={() => importFileInputRef.current?.click()}
							disabled={importing}
						>
							<Upload className="mr-2 size-4" />
							Import
						</Button>
						<input
							ref={importFileInputRef}
							type="file"
							accept=".xlsx,.xls"
							className="sr-only"
							onChange={handleImportExcel}
							disabled={importing}
						/>
					</div>
				)}
			</div>

			{isAdding && (
				<div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-6">
					<div className="flex items-center justify-between pb-4 border-b">
						<h3 className="text-xl">{editingId ? 'Edit COA Template' : 'New COA Template'}</h3>
						<span className="text-sm text-gray-500">Fill in COA template master information</span>
					</div>
					<div className="grid grid-cols-2 gap-4">
						<div>
							<Label htmlFor="batchNumber" className="text-sm text-gray-700">Batch Number</Label>
							<Input id="batchNumber" value={formData.batchNumber} onChange={(e) => setFormData({ ...formData, batchNumber: e.target.value })} placeholder="e.g. BN-20260418-01" className="mt-1" />
						</div>
						<div>
							<Label htmlFor="productDescription" className="text-sm text-gray-700">Product Description</Label>
							<Input id="productDescription" value={formData.productDescription} onChange={(e) => setFormData({ ...formData, productDescription: e.target.value })} placeholder="e.g. Microcrystalline Cellulose" className="mt-1" />
						</div>
						<div>
							<Label htmlFor="specNumber" className="text-sm text-gray-700">Spec Number</Label>
							<Input id="specNumber" value={formData.specNumber} onChange={(e) => setFormData({ ...formData, specNumber: e.target.value })} placeholder="e.g. SPEC-001" className="mt-1" />
						</div>
						<div>
							<Label htmlFor="sampleSubmissionRefNumber" className="text-sm text-gray-700">Sample Submission Ref Number</Label>
							<Input id="sampleSubmissionRefNumber" value={formData.sampleSubmissionRefNumber} onChange={(e) => setFormData({ ...formData, sampleSubmissionRefNumber: e.target.value })} placeholder="e.g. SSR-20260418-09" className="mt-1" />
						</div>
						<div>
							<Label htmlFor="manufactureDate" className="text-sm text-gray-700">Manufacture Date</Label>
							<Input id="manufactureDate" type="date" value={formData.manufactureDate ? formData.manufactureDate.slice(0, 10) : ''} onChange={(e) => setFormData({ ...formData, manufactureDate: e.target.value })} className="mt-1" />
						</div>
						<div>
							<Label htmlFor="expiryDate" className="text-sm text-gray-700">Expiry Date</Label>
							<Input id="expiryDate" type="date" value={formData.expiryDate ? formData.expiryDate.slice(0, 10) : ''} onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })} className="mt-1" />
						</div>
					</div>

					<div className="space-y-4">
						<h4 className="text-base text-gray-700 pt-2">Test Items</h4>
						<div className="bg-gray-50 rounded-lg p-4 space-y-3">
							<div className="grid grid-cols-5 gap-3 items-end">
								<div>
									<Label htmlFor="parameter" className="text-xs text-gray-600">Parameter</Label>
									<Input id="parameter" value={newItem.parameter} onChange={(e) => setNewItem({ ...newItem, parameter: e.target.value })} placeholder="e.g. Protein" className="mt-1" />
								</div>
								<div>
									<Label htmlFor="classification" className="text-xs text-gray-600">Classification</Label>
									<Input id="classification" value={newItem.classification} onChange={(e) => setNewItem({ ...newItem, classification: e.target.value })} placeholder="e.g. Mineral" className="mt-1" />
								</div>
								<div>
									<Label htmlFor="testMethod" className="text-xs text-gray-600">Test Method</Label>
									<Input id="testMethod" value={newItem.testMethod} onChange={(e) => setNewItem({ ...newItem, testMethod: e.target.value })} placeholder="e.g. HPLC" className="mt-1" />
								</div>
								<div>
									<Label htmlFor="uom" className="text-xs text-gray-600">UOM</Label>
									<Input id="uom" value={newItem.uom} onChange={(e) => setNewItem({ ...newItem, uom: e.target.value })} placeholder="e.g. %" className="mt-1" />
								</div>
								<Button onClick={handleAddItem} variant="outline" className="border-blue-300 text-blue-600 hover:bg-blue-50">
									<Plus className="size-4 mr-1" />
									Add
								</Button>
							</div>

							<div className="overflow-x-auto mt-4">
								<table className="w-full">
									<thead>
										<tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
											<th className="px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Parameter</th>
											<th className="px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Classification</th>
											<th className="px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Test Method</th>
											<th className="px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">UOM</th>
											<th className="px-4 py-3 text-center text-xs text-gray-600 uppercase tracking-wider">Actions</th>
										</tr>
									</thead>
									<tbody className="divide-y divide-gray-100">
										{formData.items.map((item, index) => (
											<tr key={index} className="hover:bg-gray-50">
												<td className="px-4 py-3 text-sm text-gray-900">{item.parameter}</td>
												<td className="px-4 py-3 text-sm text-gray-600">{item.classification}</td>
												<td className="px-4 py-3 text-sm text-gray-600">{item.testMethod}</td>
												<td className="px-4 py-3 text-sm text-gray-600">{item.uom}</td>
												<td className="px-4 py-3 text-center">
													<Button variant="ghost" size="sm" onClick={() => handleRemoveItem(index)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
														<Trash2 className="size-4" />
													</Button>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					</div>

					<div className="flex items-center justify-end gap-3 pt-4 border-t">
						<Button variant="outline" onClick={handleCancel} disabled={saving}>
							<X className="size-4 mr-2" />
							Cancel
						</Button>
						<Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
							<Save className="size-4 mr-2" />
							{saving ? 'Saving...' : 'Save COA Template'}
						</Button>
					</div>
				</div>
			)}

			{!isAdding && (
				<div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
					<div className="border-b border-gray-200 bg-slate-50 px-4 py-3">
						<div className="flex flex-wrap items-center gap-2">
							<Label htmlFor="list-batch-number" className="text-sm text-gray-700">Batch Number</Label>
							<Input
								id="list-batch-number"
								value={batchNumberQuery}
								onChange={(e) => setBatchNumberQuery(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === 'Enter') {
										e.preventDefault();
										handleSearchByBatchNumber();
									}
								}}
								placeholder="Search batch number"
								className="w-full max-w-xs bg-white"
							/>
							<Button type="button" variant="outline" onClick={handleSearchByBatchNumber}>Search</Button>
							<Button type="button" variant="ghost" onClick={handleResetBatchNumberSearch}>Reset</Button>
						</div>
					</div>
					<div>
						<table className="w-full table-fixed">
							<thead>
								<tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
									<th className="w-[12%] px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Batch Number</th>
									<th className="w-[20%] px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Product Description</th>
									<th className="w-[13%] px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Manufacture Date</th>
									<th className="w-[15%] px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Spec Number</th>
									<th className="w-[13%] px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Expiry Date</th>
									<th className="w-[17%] px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Sample Submission Ref Number</th>
									<th className="w-[10%] px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Actions</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-gray-100">
								{templatesState.map((spec) => (
									<tr key={spec.id} className="hover:bg-gray-50 transition-colors">
										<td className="px-4 py-3 text-sm text-gray-900"><span className="block truncate" title={spec.batchNumber}>{spec.batchNumber}</span></td>
										<td className="px-4 py-3 text-sm text-gray-900"><span className="block truncate" title={spec.productDescription}>{spec.productDescription}</span></td>
										<td className="px-4 py-3 text-sm text-gray-600">{new Date(spec.manufactureDate).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })}</td>
										<td className="px-4 py-3 text-sm text-gray-900"><span className="block truncate" title={spec.specNumber}>{spec.specNumber}</span></td>
										<td className="px-4 py-3 text-sm text-gray-600">{new Date(spec.expiryDate).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })}</td>
										<td className="px-4 py-3 text-sm text-gray-900"><span className="block truncate" title={spec.sampleSubmissionRefNumber}>{spec.sampleSubmissionRefNumber}</span></td>
										<td className="px-4 py-3 text-right">
											<div className="flex gap-2 justify-end">
												<Button variant="ghost" size="sm" onClick={() => handleOpenDetail(spec.id)} className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50" disabled={loadingDetail} title="Details">
													<Eye className="size-4" />
												</Button>
												<Button variant="ghost" size="sm" onClick={() => handleDeleteClick(spec.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" title="Delete">
													<Trash2 className="size-4" />
												</Button>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			)}

			{!loadingList && templatesState.length === 0 && !isAdding && (
				<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
					<FileText className="size-16 mx-auto text-gray-300 mb-4" />
					<h3 className="text-lg text-gray-600 mb-2">No COA templates yet</h3>
					<p className="text-sm text-gray-400 mb-6">Please import COA template data from Excel.</p>
				</div>
			)}

			<Confirm
				isOpen={deleteConfirmOpen}
				loading={false}
				title="Confirm Delete"
				content="Are you sure you want to delete this COA template record?"
				onConfirm={handleConfirmDelete}
				onClose={() => {
					setDeleteConfirmOpen(false);
					setPendingDeleteId(null);
				}}
			/>
		</div>
	);
}
